import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Loader } from "lucide-react";

const NETWORKS = [
  { id: "sg", label: "SGD · PayNow", region: "Singapore", ms: 620 },
  { id: "in", label: "INR · UPI", region: "India", ms: 940 },
  { id: "eu", label: "EUR · SEPA", region: "Europe", ms: 1180 },
  { id: "us", label: "USD · Wire", region: "USA", ms: 1420 },
  { id: "ph", label: "PHP · Wallet", region: "Philippines", ms: 780 },
  { id: "ae", label: "AED · Bank", region: "UAE", ms: 1020 },
];

const STAGES = [
  "INITIATED",
  "VALIDATED",
  "ROUTING",
  "SETTLED",
  "CONFIRMED",
];

const SVG_NETWORKS = [
  { id: "paynow", x: 560, y: 40, label: "PAYNOW · SGD" },
  { id: "upi", x: 580, y: 105, label: "UPI · INR" },
  { id: "sepa", x: 590, y: 170, label: "SEPA · EUR" },
  { id: "wire", x: 580, y: 235, label: "WIRE · USD" },
  { id: "wallet", x: 560, y: 285, label: "WALLET · PHP" },
];

export default function TransactionDemo() {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 2600);

    return () => clearInterval(interval);
  }, []);

  const active = NETWORKS[tick % NETWORKS.length];
  const stage = STAGES[(tick + 2) % STAGES.length];

  return (
    <div
      data-testid="transaction-demo"
      className="relative w-full h-full min-h-[500px] rounded-none overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, rgba(13,20,43,0.9) 0%, rgba(10,15,31,0.95) 100%)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <div className="grain" />

      <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 relative z-10">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-[color:var(--p10-electric)] tx-glow" />
          <span className="font-mono-tech text-[11px] tracking-[0.24em] uppercase text-white/70">
            Live · payments.pay10x.io
          </span>
        </div>

        <div className="font-mono-tech text-[11px] text-white/40">
          TX-{(1000000 + tick).toString(16).toUpperCase()}
        </div>
      </div>

      <div className="relative px-6 pt-6 pb-2 z-10">
        <svg viewBox="0 0 640 300" className="w-full h-[280px]">
          <defs>
            <linearGradient id="pipe" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#2E7BFF" stopOpacity="0.1" />
              <stop offset="50%" stopColor="#2E7BFF" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#2E7BFF" stopOpacity="0.1" />
            </linearGradient>

            <radialGradient id="hub" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#2E7BFF" />
              <stop offset="100%" stopColor="#1D3AF2" />
            </radialGradient>
          </defs>

          <g>
            <rect
              x="10"
              y="130"
              width="120"
              height="40"
              rx="0"
              fill="none"
              stroke="rgba(255,255,255,0.25)"
            />

            <text
              x="70"
              y="146"
              textAnchor="middle"
              fill="#fff"
              fontFamily="JetBrains Mono"
              fontSize="10"
              letterSpacing="2"
            >
              CLIENT · API
            </text>

            <text
              x="70"
              y="162"
              textAnchor="middle"
              fill="rgba(255,255,255,0.5)"
              fontFamily="JetBrains Mono"
              fontSize="9"
            >
              POST /v1/pay
            </text>
          </g>

          <line
            x1="130"
            y1="150"
            x2="270"
            y2="150"
            stroke="url(#pipe)"
            strokeWidth="2"
            className="tx-dash"
          />

          <g transform="translate(320,150)">
            <circle r="60" fill="rgba(46,123,255,0.06)" className="tx-ring" />
            <circle r="60" fill="rgba(46,123,255,0.06)" className="tx-ring tx-ring-2" />
            <circle r="60" fill="rgba(46,123,255,0.06)" className="tx-ring tx-ring-3" />
            <circle r="42" fill="url(#hub)" />

            <text
              y="-2"
              textAnchor="middle"
              fill="#fff"
              fontFamily="Cormorant Garamond"
              fontSize="20"
              fontWeight="500"
            >
              pay10x
            </text>

            <text
              y="14"
              textAnchor="middle"
              fill="rgba(255,255,255,0.7)"
              fontFamily="JetBrains Mono"
              fontSize="8"
              letterSpacing="2"
            >
              ROUTER
            </text>
          </g>

          {SVG_NETWORKS.map((network, index) => (
            <g key={network.id}>
              <line
                x1="378"
                y1="150"
                x2={network.x - 10}
                y2={network.y}
                stroke="url(#pipe)"
                strokeWidth="1.2"
                className="tx-dash"
                style={{ animationDelay: `${index * 0.2}s` }}
              />

              <circle
                cx={network.x}
                cy={network.y}
                r="4"
                fill={index === tick % 5 ? "#2E7BFF" : "rgba(255,255,255,0.3)"}
                className={index === tick % 5 ? "tx-glow" : ""}
              />

              <text
                x={network.x + 12}
                y={network.y + 3}
                fill="rgba(255,255,255,0.65)"
                fontFamily="JetBrains Mono"
                fontSize="9"
                letterSpacing="1.5"
              >
                {network.label}
              </text>
            </g>
          ))}
        </svg>
      </div>

      <div className="relative z-10 px-6 pb-6 pt-3 border-t border-white/10">
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.24em] text-white/40">
            Status feed
          </span>

          <span className="font-mono-tech text-[10px] text-white/40">
            {active.ms}ms · normalized
          </span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tick}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="flex items-center justify-between gap-4"
          >
            {/* Remaining JSX stays exactly the same */}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}